import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../../lib/api";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { Button, Input, Field, Select, Spinner } from "../ui";
import { ActionList } from "./ActionList";
import { ADVISOR_ICON } from "./AdvisorCard";
import { MessagesSquare, Sparkles, Gavel, ArrowRight, Save, AlertTriangle, CheckCircle2 } from "lucide-react";

const REC_STYLE = {
  proceed: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  proceed_with_conditions: "bg-teal-500/15 text-teal-500 border-teal-500/30",
  delay: "bg-amber-500/15 text-amber-500 border-amber-500/30",
  reject: "bg-red-500/15 text-red-500 border-red-500/30",
  needs_more_data: "bg-text-secondary/15 text-text-secondary border-text-secondary/30",
};
const WORKING = { summarizing: "bm_status_summarizing", questioning: "bm_status_questioning", reviewing: "bm_status_reviewing", deliberating: "bm_status_deliberating" };

function Textarea(props) {
  return <textarea {...props} className={cn("w-full rounded-lg border bg-surface px-3 py-2 text-sm outline-none focus:border-primary/50 transition-colors", props.className)} />;
}

function AgendaForm({ meta, onCreated }) {
  const { t } = useApp();
  const [f, setF] = useState({ title: "", question: "", entity_id: "", desired_outcome: "", constraints: "" });
  const [advisors, setAdvisors] = useState([]);
  const [linked, setLinked] = useState({ project: "", goal: "", deal: "" });
  const [opts, setOpts] = useState({ projects: [], goals: [], deals: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!f.entity_id) { setOpts({ projects: [], goals: [], deals: [] }); return; }
    Promise.all([
      api.get(`/space-projects?entity_id=${f.entity_id}`).then((r) => r.data).catch(() => []),
      api.get(`/goals?entity_id=${f.entity_id}`).then((r) => r.data).catch(() => []),
      api.get(`/entities/${f.entity_id}/pipeline`).then((r) => r.data.deals || []).catch(() => []),
    ]).then(([projects, goals, deals]) => setOpts({ projects, goals, deals }));
    setLinked({ project: "", goal: "", deal: "" });
  }, [f.entity_id]);

  const toggleAdvisor = (k) => setAdvisors((p) => p.includes(k) ? p.filter((x) => x !== k) : [...p, k]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/advisory/meetings", {
        title: f.title, question: f.question, entity_id: f.entity_id || null,
        desired_outcome: f.desired_outcome, constraints: f.constraints,
        selected_advisor_keys: advisors,
        linked_project_id: linked.project || null, linked_goal_id: linked.goal || null, linked_deal_id: linked.deal || null,
      });
      onCreated(data);
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-surface rounded-xl border p-5 space-y-4" data-testid="bm-agenda-form">
      <h2 className="font-heading font-bold text-base flex items-center gap-2"><MessagesSquare className="w-4 h-4" />{t("bm_agenda")}</h2>
      <Field label={t("bm_title")}><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="bm-title" /></Field>
      <Field label={t("bm_question")}><Textarea rows={3} value={f.question} onChange={(e) => setF({ ...f, question: e.target.value })} data-testid="bm-question" /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={t("bm_entity")}>
          <Select value={f.entity_id} onChange={(e) => setF({ ...f, entity_id: e.target.value })} data-testid="bm-entity">
            <option value="">{t("bm_none")}</option>
            {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </Select>
        </Field>
        {f.entity_id && opts.projects.length > 0 && (
          <Field label={t("bm_linked_project")}>
            <Select value={linked.project} onChange={(e) => setLinked({ ...linked, project: e.target.value })} data-testid="bm-linked-project">
              <option value="">{t("bm_none")}</option>{opts.projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </Select>
          </Field>
        )}
        {f.entity_id && opts.deals.length > 0 && (
          <Field label={t("bm_linked_deal")}>
            <Select value={linked.deal} onChange={(e) => setLinked({ ...linked, deal: e.target.value })} data-testid="bm-linked-deal">
              <option value="">{t("bm_none")}</option>{opts.deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </Select>
          </Field>
        )}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label={t("bm_outcome")}><Textarea rows={2} value={f.desired_outcome} onChange={(e) => setF({ ...f, desired_outcome: e.target.value })} data-testid="bm-outcome" /></Field>
        <Field label={t("bm_constraints")}><Textarea rows={2} value={f.constraints} onChange={(e) => setF({ ...f, constraints: e.target.value })} data-testid="bm-constraints" /></Field>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary">{t("bm_advisors_optional")}</label>
        <p className="text-xs text-text-secondary mb-1.5">{t("bm_advisors_hint")}</p>
        <div className="flex flex-wrap gap-1.5">
          {meta.advisors.filter((a) => a.key !== "chief_of_staff").map((a) => {
            const on = advisors.includes(a.key); const Icon = ADVISOR_ICON[a.key] || MessagesSquare;
            return (
              <button key={a.key} onClick={() => toggleAdvisor(a.key)} data-testid={`bm-advisor-${a.key}`}
                className={cn("text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1.5 transition-colors",
                  on ? "border-primary text-primary bg-primary/5" : "text-text-secondary hover:border-primary/40")}>
                <Icon className="w-3 h-3" />{a.name}
              </button>
            );
          })}
        </div>
      </div>
      <Button onClick={submit} disabled={saving || !f.title.trim() || !f.question.trim()} data-testid="bm-create-btn">
        <Sparkles className="w-4 h-4 mr-1.5" />{t("bm_create")}
      </Button>
    </div>
  );
}

function Working({ status }) {
  const { t } = useApp();
  return (
    <div className="bg-surface rounded-xl border p-10 flex flex-col items-center text-center" data-testid="bm-working">
      <Spinner className="w-8 h-8 text-primary" />
      <p className="text-sm font-medium mt-4">{t(WORKING[status] || "bm_working")}</p>
      <p className="text-xs text-text-secondary mt-1">{t("adv_generating_hint")}</p>
    </div>
  );
}

function Bullets({ label, items, color }) {
  if (!items?.length) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary mb-1.5">{label}</p>
      <ul className="space-y-1">{items.map((x, i) => <li key={i} className={cn("text-sm leading-snug flex gap-2", color)}><span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-current shrink-0 opacity-60" />{x}</li>)}</ul>
    </div>
  );
}

function SummaryView({ meeting, onStart }) {
  const { t } = useApp();
  const cs = meeting.cos_summary || {};
  const [edit, setEdit] = useState({ proposing: cs.proposing, why: cs.why, upside: cs.upside, downside: cs.downside });
  const [busy, setBusy] = useState(false);
  const confirm = async () => {
    setBusy(true);
    try {
      await api.patch(`/advisory/meetings/${meeting.id}/summary`, { cos_summary: edit });
      await api.post(`/advisory/meetings/${meeting.id}/start`);
      onStart();
    } finally { setBusy(false); }
  };
  const Row = ({ label, k }) => (
    <Field label={label}><Textarea rows={2} value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })} data-testid={`bm-summary-${k}`} /></Field>
  );
  return (
    <div className="bg-surface rounded-xl border p-5 space-y-4" data-testid="bm-summary">
      <h2 className="font-heading font-bold text-base flex items-center gap-2"><Sparkles className="w-4 h-4 text-primary" />{t("bm_summary_title")}</h2>
      <Row label={t("bm_proposing")} k="proposing" />
      <div className="grid sm:grid-cols-2 gap-3">
        <Row label={t("bm_why")} k="why" />
        <Row label={t("bm_upside")} k="upside" />
      </div>
      <Row label={t("bm_downside")} k="downside" />
      <div className="grid sm:grid-cols-2 gap-4">
        <Bullets label={t("bm_known_data")} items={cs.known_data} color="text-text-primary" />
        <Bullets label={t("bm_missing")} items={cs.missing_assumptions} color="text-amber-500" />
      </div>
      <Button onClick={confirm} disabled={busy} data-testid="bm-confirm-start-btn"><ArrowRight className="w-4 h-4 mr-1.5" />{t("bm_confirm_start")}</Button>
    </div>
  );
}

function QuestionsView({ meeting, onSubmitted }) {
  const { t } = useApp();
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/advisory/meetings/${meeting.id}/answers`, { answers }); onSubmitted(); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="bm-questions">
      <h2 className="font-heading font-bold text-base flex items-center gap-2"><MessagesSquare className="w-4 h-4" />{t("bm_questions_title")}</h2>
      {meeting.questions.map((q) => {
        const Icon = ADVISOR_ICON[q.advisor_key] || MessagesSquare;
        return (
          <div key={q.id} className="bg-surface rounded-xl border p-4" data-testid={`bm-question-${q.id}`}>
            <p className="text-xs font-semibold text-text-secondary flex items-center gap-1.5 mb-1"><Icon className="w-3.5 h-3.5" />{q.advisor_name}</p>
            <p className="text-sm font-medium mb-2 leading-snug">{q.question}</p>
            <Textarea rows={2} value={answers[q.id] || ""} placeholder={t("bm_answer_ph")} data-testid={`bm-answer-${q.id}`}
              onChange={(e) => setAnswers({ ...answers, [q.id]: e.target.value })} />
          </div>
        );
      })}
      <Button onClick={submit} disabled={busy} data-testid="bm-submit-answers-btn"><ArrowRight className="w-4 h-4 mr-1.5" />{t("bm_submit_answers")}</Button>
    </div>
  );
}

function FollowupView({ meeting, onVerdict }) {
  const { t } = useApp();
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/advisory/meetings/${meeting.id}/verdict`, { followup_answers: answers }); onVerdict(); }
    finally { setBusy(false); }
  };
  const hasFollowups = meeting.followups?.length > 0;
  return (
    <div className="space-y-3" data-testid="bm-followups">
      {hasFollowups && <h2 className="font-heading font-bold text-base flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" />{t("bm_followups_title")}</h2>}
      {meeting.followups.map((f) => {
        const Icon = ADVISOR_ICON[f.advisor_key] || MessagesSquare;
        return (
          <div key={f.id} className="bg-surface rounded-xl border border-l-4 border-l-amber-500/50 p-4" data-testid={`bm-followup-${f.id}`}>
            <p className="text-xs font-semibold text-text-secondary flex items-center gap-1.5 mb-1"><Icon className="w-3.5 h-3.5" />{f.advisor_name}</p>
            <p className="text-sm font-medium leading-snug">{f.question}</p>
            {f.reason && <p className="text-xs text-amber-500 mt-0.5">{f.reason}</p>}
            <Textarea rows={2} value={answers[f.id] || ""} placeholder={t("bm_answer_ph")} className="mt-2" data-testid={`bm-followup-answer-${f.id}`}
              onChange={(e) => setAnswers({ ...answers, [f.id]: e.target.value })} />
          </div>
        );
      })}
      <Button onClick={go} disabled={busy} data-testid="bm-verdict-btn">
        <Gavel className="w-4 h-4 mr-1.5" />{hasFollowups ? t("bm_get_verdict") : t("bm_skip_followups")}
      </Button>
    </div>
  );
}

function VerdictView({ meeting, meta, reload, onClosed }) {
  const { t, lang } = useApp();
  const v = meeting.verdict || {};
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.post(`/advisory/meetings/${meeting.id}/close`, {}); onClosed(); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-5" data-testid="bm-verdict">
      <div className="bg-surface rounded-xl border p-5">
        <div className="flex items-center gap-3 flex-wrap">
          <Gavel className="w-5 h-5 text-primary" />
          <span data-testid="bm-recommendation" className={cn("text-sm font-bold px-3 py-1 rounded-full border", REC_STYLE[v.recommendation])}>
            {t(`rec_${v.recommendation}`)}
          </span>
          <div className="flex items-center gap-2">
            <span className="text-xs text-text-secondary">{t("bm_confidence")}</span>
            <div className="w-28 h-2 rounded-full bg-surface-hover overflow-hidden"><div className="h-full bg-primary" style={{ width: `${v.confidence || 0}%` }} /></div>
            <span className="text-xs font-semibold tabular-nums">{v.confidence}%</span>
          </div>
          {meeting.status === "closed" && <span className="ml-auto text-xs text-emerald-500 inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />{t("bm_saved")}</span>}
        </div>
        <p className="text-sm mt-3 leading-relaxed">{v.final_recommendation}</p>
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          <Bullets label={t("bm_strong")} items={v.strong_assumptions} color="text-emerald-500" />
          <Bullets label={t("bm_weak")} items={v.weak_assumptions} color="text-amber-500" />
          <Bullets label={t("bm_contradictions")} items={v.contradictions} color="text-red-500" />
          <Bullets label={t("bm_unanswered")} items={v.unanswered_questions} color="text-text-secondary" />
          <Bullets label={t("bm_data_for")} items={v.data_supports} color="text-emerald-500" />
          <Bullets label={t("bm_data_against")} items={v.data_against} color="text-red-500" />
          <Bullets label={t("bm_risks")} items={v.risks} color="text-red-500" />
          <Bullets label={t("bm_suggested_docs")} items={v.suggested_documents} color="text-text-secondary" />
        </div>
        {v.cheapest_test && <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/15"><p className="text-[10px] uppercase tracking-wider font-semibold text-primary mb-1">{t("bm_cheapest")}</p><p className="text-sm">{v.cheapest_test}</p></div>}
        {v.advisor_objections?.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-text-secondary mb-1.5">{t("bm_objections")}</p>
            <div className="space-y-1.5">{v.advisor_objections.map((o, i) => <p key={i} className="text-sm leading-snug"><span className="font-semibold">{o.advisor_name}:</span> <span className="text-text-secondary">{o.objection}</span></p>)}</div>
          </div>
        )}
        {(v.follow_up_date || v.decision_log_entry) && (
          <div className="mt-4 pt-4 border-t">
            {v.follow_up_date && <p className="text-xs text-text-secondary">{t("bm_followup_date")}: <span className="font-medium text-text-primary">{new Date(v.follow_up_date).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US")}</span></p>}
            {v.decision_log_entry && <p className="text-sm mt-2 italic text-text-secondary">{v.decision_log_entry}</p>}
          </div>
        )}
      </div>

      <ActionList actions={meeting.actions} actionBase={`/advisory/meetings/${meeting.id}`} meta={meta} onChange={reload} />

      {meeting.status !== "closed" && (
        <Button onClick={save} disabled={busy} data-testid="bm-save-log-btn"><Save className="w-4 h-4 mr-1.5" />{t("bm_save_log")}</Button>
      )}
    </div>
  );
}

export default function BoardMeeting({ meta, activeMeeting, setActiveMeeting, onSaved }) {
  const { t } = useApp();
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);
  const meeting = activeMeeting;
  const status = meeting?.status;

  const refetch = useCallback(async (id) => {
    const { data } = await api.get(`/advisory/meetings/${id}`);
    setActiveMeeting(data);
    return data;
  }, [setActiveMeeting]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // Poll while the board is working
  useEffect(() => {
    if (!meeting?.id) return;
    const working = ["summarizing", "questioning", "reviewing", "deliberating"].includes(status);
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (working) {
      pollRef.current = setInterval(async () => {
        const d = await refetch(meeting.id);
        if (!["summarizing", "questioning", "reviewing", "deliberating"].includes(d.status)) {
          clearInterval(pollRef.current); pollRef.current = null;
          if (d.status !== "summarizing") onSaved?.();
        }
      }, 4000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [meeting?.id, status, refetch, onSaved]);

  if (!meeting && !starting) {
    return (
      <div className="bg-surface rounded-xl border border-dashed p-12 flex flex-col items-center text-center" data-testid="bm-start">
        <div className="w-12 h-12 rounded-xl bg-surface-hover flex items-center justify-center mb-4"><MessagesSquare className="w-6 h-6 text-text-secondary" /></div>
        <h3 className="font-heading font-bold text-base">{t("adv_tab_board")}</h3>
        <p className="text-sm text-text-secondary mt-1.5 max-w-md">{t("adv_board_soon_desc")}</p>
        <Button className="mt-5" onClick={() => setStarting(true)} data-testid="bm-start-btn"><MessagesSquare className="w-4 h-4 mr-1.5" />{t("bm_start")}</Button>
      </div>
    );
  }

  if (!meeting && starting) return <AgendaForm meta={meta} onCreated={(m) => { setStarting(false); setActiveMeeting(m); }} />;

  const reset = () => { setActiveMeeting(null); setStarting(false); };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={reset} className="text-xs text-text-secondary hover:text-text-primary" data-testid="bm-new-btn">+ {t("bm_new")}</button>
        <span className="text-sm font-semibold truncate">{meeting.title}</span>
      </div>
      {["summarizing", "questioning", "reviewing", "deliberating"].includes(status) && <Working status={status} />}
      {status === "summary_ready" && <SummaryView meeting={meeting} onStart={() => refetch(meeting.id)} />}
      {status === "questions_ready" && <QuestionsView meeting={meeting} onSubmitted={() => refetch(meeting.id)} />}
      {status === "followup_ready" && <FollowupView meeting={meeting} onVerdict={() => refetch(meeting.id)} />}
      {(status === "verdict_ready" || status === "closed") && <VerdictView meeting={meeting} meta={meta} reload={() => refetch(meeting.id)} onClosed={() => { refetch(meeting.id); onSaved?.(); }} />}
      {status === "error" && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-5 text-sm text-red-500" data-testid="bm-error">{t("adv_error")}</div>}
    </div>
  );
}
